import { _MiscMessages, ConfigSystemParams, MdmDeviceClass, WidgetTitles } from 'src/app/core/consts';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { catchError, combineLatest, debounceTime, distinctUntilChanged, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { IntegerResultAndPercentageChangeDTO, IntegerResultAndPercentageChangeDTO_EmptyObject } from 'src/app/core/interfaces/general/integer-result-and-percentage-change';
import { IntegerResultAndStringResultDTO, IntegerResultAndStringResultDTO_EmptyObject } from 'src/app/core/interfaces/general/integer-result-and-string-result';
import { IntegerResultDTO, IntegerResultDTO_EmptyObject } from 'src/app/core/interfaces/general/integer-result';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';
import { WidgetTitleBox } from 'src/app/core/interfaces/general/widget-title-box';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-widget-row-mdm-device-compliance-01',
  templateUrl: './widget-row-mdm-device-compliance-01.component.html',
  styleUrls: ['./widget-row-mdm-device-compliance-01.component.scss'],
  providers: [TippyToolTipService]
})

export class WidgetRowMdmDeviceCompliance01Component implements AfterViewInit, OnChanges, OnDestroy, OnInit {

  @Input() allowContainerClose: boolean | undefined;
  @Input() containerTitle     : string  | undefined;
  @Input() deviceClass        : string  | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() showStatTenant     : boolean | undefined;
  @Input() showTitleHintText  : boolean | undefined;
  @Input() useContainer       : boolean | undefined;
    
  @Output() containerClosed: EventEmitter<void>=new EventEmitter<void>();
    
  get isOtherClass() { return this.deviceClass===MdmDeviceClass.other; }

  get pollingWidgets() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.WidgetRow_Polling_Compliance_MdmDevices01, false); }

  private filterCompanyID        : number | undefined;
  private filterDate             : OurDate | undefined=populateOurDate(this.exceptionsService, Date.now());
  private statTitleCompanyDetails: string=WidgetTitles.mdmDeviceComplianceCompanyDetails;
  private statTitleLatestAndroid : string=WidgetTitles.mdmDeviceComplianceLatestAndroid;
  private statTitleLatestIos     : string=WidgetTitles.mdmDeviceComplianceLatestIos;
  private statTitleTotalDevices  : string=WidgetTitles.mdmDeviceComplianceTotalDevices;

  private destroy$  =new Subject<void>();
  private selection$=new Subject<{ companyId: number; date: { year: number; month: number; day: number }; refreshToken?: number; }>();
  
  public expandContainer : boolean=true;
  public statData!       : Array<WidgetTitleBox>;
  public visibleContainer: boolean=true;

  public readonly mobileOnlyLabels   : string[]=[WidgetTitles.mdmDeviceComplianceLatestIos, WidgetTitles.mdmDeviceComplianceLatestAndroid];
  public readonly otherDevicesCaption: string="Other Devices";
  public readonly totalDevicesLabel  : string=WidgetTitles.mdmDeviceComplianceTotalDevices;
  
  constructor(
    private changeDetectorRef  : ChangeDetectorRef,
    private configSystemService: ConfigSystemService,
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService,
    private widgetService      : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      this.initSelectionStream();
      
      if (!this.containerTitle || this.containerTitle.trim()=="")
        this.containerTitle=_MiscMessages.TitleNotSet;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnChanges(changes: SimpleChanges): void {
    try {
      if (changes['deviceClass'] && !changes['deviceClass'].firstChange && (this.filterCompanyID ?? -1)!=-1)
        this.refreshWidgets();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      setTimeout(() => {
        this.displayData();
      });
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.destroy$.next();
      this.destroy$.complete();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public refreshWidgets(): void {
    try {
      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
  
      this.selection$.next({ companyId, date, refreshToken: Date.now() });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private displayData() {
    try {
      this.statData=[];
                   
      let statTenant: WidgetTitleBox={
        accentColour   : "success",
        id             : 1,
        badge          : "ri-arrow-up-s-line",
        bgColor        : "bg-success",
        caption        : undefined,
        captionClass   : "white-50",
        counter        : undefined,
        counterAsString: "",
        counterClass   : "text-white",
        decimals       : 1,
        icon           : "bx bx-buildings",
        iconClass      : "bg-white bg-opacity-25",
        label          : this.statTitleCompanyDetails,
        labelClass     : "white-50",
        percentage     : undefined,
        percentageClass: "bg-white bg-opacity-25",
        prefix         : "",
        separator      : ",",
        suffix         : "",
      }
                   
      let statTotalDevices: WidgetTitleBox={
        accentColour   : "success",
        id             : 2,
        badge          : "ri-arrow-up-s-line",
        caption        : "Android & iOS",
        captionClass   : "muted",
        counter        : 0,
        counterAsString: undefined,
        decimals       : 0,
        icon           : "bx bx-devices",
        iconClass      : "bg-transparent text-primary",
        label          : this.statTitleTotalDevices,
        labelClass     : "muted",
        percentage     : "0%",
        percentageClass: "bg-success-subtle text-success",
        prefix         : "",
        separator      : ",",
        suffix         : "",
      }
                   
      let statLatestIos: WidgetTitleBox={
        accentColour   : "info",
        id             : 3,
        badge          : "ri-arrow-up-s-line",
        caption        : "Apple Devices",
        captionClass   : "muted",
        counter        : 0,
        counterAsString: undefined,
        decimals       : 2,
        icon           : "bx bxl-apple",
        iconClass      : "bg-transparent text-info",
        label          : this.statTitleLatestIos,
        labelClass     : "muted",
        percentage     : undefined,
        percentageClass: "bg-success-subtle text-success",
        prefix         : "",
        separator      : ",",
        suffix         : "",
      }
                   
      let statLatestAndroid: WidgetTitleBox={
        accentColour   : "purple",
        id             : 4,
        badge          : "ri-arrow-up-s-line",
        caption        : "Google Devices",
        captionClass   : "muted",
        counter        : 0,
        counterAsString: undefined,
        decimals       : 0,
        icon           : "bx bxl-android",
        iconClass      : "bg-transparent text-purple",
        label          : this.statTitleLatestAndroid,
        labelClass     : "muted",
        percentage     : undefined,
        percentageClass: "bg-success-subtle text-success",
        prefix         : "",
        separator      : ",",
        suffix         : "",
      }
          
      if (this.showStatTenant ?? true)
        this.statData.push(statTenant);

      this.statData.push(statTotalDevices);
      this.statData.push(statLatestAndroid);
      this.statData.push(statLatestIos);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
       return (undefined);
    }
  }
  
  private initSelectionStream(): void {
    try {
      this.selection$
      .pipe(
        debounceTime(150),
        distinctUntilChanged((a, b) =>
          a.companyId   ===b.companyId  &&
          a.date.year   ===b.date.year  &&
          a.date.month  ===b.date.month &&
          a.date.day    ===b.date.day   &&
          a.refreshToken===b.refreshToken
        ),
        switchMap(selected => {
          const companyId=selected.companyId;
          const year     =selected.date.year;
          const month    =selected.date.month;
          const day      =selected.date.day;
          
          const safeStart=<T>(observable: Observable<T>, fallback: T) =>
            observable.pipe(
              startWith(fallback),
              catchError(err => {
                this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(err)));
                return of(fallback);
              })
            );
                    
          const companyDetailsSource=this.widgetService.getCompanyDetails(companyId);
          const latestAndroidSource =this.widgetService.getLatestAndroid();
          const latestIosSource     =this.widgetService.getLatestIos();
                    
          const totalDevicesSource=this.pollingWidgets
                                   ? this.widgetService.getIntuneDevicesTotalCountUpdates(companyId, false, this.deviceClass)
                                   : this.widgetService.getIntuneDevicesTotalCount       (companyId, false, this.deviceClass);

          return combineLatest({
            companyDetails: safeStart(companyDetailsSource, IntegerResultAndStringResultDTO_EmptyObject()),
            lastestAndroid: safeStart(latestAndroidSource,  IntegerResultDTO_EmptyObject()),
            lastestIos    : safeStart(latestIosSource,      IntegerResultDTO_EmptyObject()),
            totalDevices  : safeStart(totalDevicesSource,   IntegerResultAndPercentageChangeDTO_EmptyObject()),
          });
          // .pipe(
          //   catchError((error) => {
          //     this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)));
          //     return EMPTY; //👈 CRN_20260209 - If one fails, nothing is returned
          //   })
          // );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(result => {
        this.applyCompanyDetails(result.companyDetails);
        this.applyLastestAndroid(result.lastestAndroid);
        this.applyLastestIos    (result.lastestIos);
        this.applyTotalDevices  (result.totalDevices);
        this.changeDetectorRef.markForCheck();
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
    
  public emitSelection(filterCompanyID: number | undefined, filterDate: OurDate | undefined): void {
    try {
      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      const companyId=this.filterCompanyID ?? -1;
      const date     ={
        year : <number>(this.filterDate?.year  ?? 1),
        month: <number>(this.filterDate?.month ?? 1),
        day  : <number>(this.filterDate?.day   ?? 1),
      };
      
      this.selection$.next({ companyId, date });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyCompanyDetails(data: IntegerResultAndStringResultDTO) {
    try {
      if ((this.showStatTenant ?? true)==false)
        return;

      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: WidgetTitleBox) => {
        if (stat.label==this.statTitleCompanyDetails) {
          stat.caption        =`Company ID ${data.integerResult}`,
          stat.counterAsString=data.stringResult
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyLastestAndroid(data: IntegerResultDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: WidgetTitleBox) => {
        if (stat.label==this.statTitleLatestAndroid) {
          stat.counter=data.result
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyLastestIos(data: IntegerResultDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: WidgetTitleBox) => {
        if (stat.label==this.statTitleLatestIos) {
          stat.counter=data.result
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private applyTotalDevices(data: IntegerResultAndPercentageChangeDTO) {
    try {
      if (!this.statData)
        this.displayData();
  
      this.statData.forEach((stat: WidgetTitleBox) => {
        if (stat.label==this.statTitleTotalDevices) {
          stat.badge          =data.percentageChange==null || data.percentageChange==0
                               ? "" : (data.percentageChange>0 ? "ri-arrow-up-s-line"
                               : "ri-arrow-down-s-line"),
          stat.counter        =data.result,
          stat.percentage     =data.percentageChange==null ? undefined : `${data.percentageChange}%`,
          stat.trendPercent   =data.percentageChange ?? undefined,
          stat.percentageClass=data.percentageChange==null || data.percentageChange==0
                               ? "bg-light text-muted"
                               : (data.percentageChange>0 ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger")
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public restoreContainer(): void {
    try {
      this.visibleContainer=true;
      this.changeDetectorRef.markForCheck();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
